import { recoverTerminalReturn } from './TerminalReturnService';
/**
 * Session Reporting Service
 *
 * Handles reporting of verification session results back to portal-api using
 * public API endpoints with API key authentication. This service ensures that
 * session completion status is properly recorded for analytics and billing purposes.
 *
 * Phase 3 Implementation:
 * - Uses public API endpoints instead of internal service calls
 * - API key authentication for secure communication
 * - Device information enrichment for analytics
 * - Duplicate prevention with session tracking
 * - Graceful error handling to avoid breaking user flow
 *
 * @author SafePassage Engineering
 * @version 1.0.0
 */

import { logger } from '../utils/logger';
import { runtimeConfig } from './RuntimeConfigService';
import { getDevBrandSlug } from '../contexts/DevBrandContext';
import { sanitizeBrowserMetadata } from '../utils/browserSafety';
import {
  isDefinitiveLifecycleRejection,
  isExpiredLifecycleError,
  LifecycleRequestError,
  verificationLifecycleService,
  type LifecycleProjection,
} from './VerificationLifecycleService';

const reportingLogger = logger.clone({ labels: { component: 'SessionReporting' }});

/**
 * Session result data structure
 *
 * @interface SessionResult
 * @property {string} sessionId - Unique session identifier
 * @property {'verified' | 'failed'} status - Final session status
 * @property {Object} [verificationData] - Additional verification metadata
 */
interface SessionResult {
  sessionId: string;
  status: 'verified' | 'failed' | 'cancelled';
  verificationData?: {
    estimatedAge?: number;
    method?: string;
    reason?: string;
    deviceType?: string;
    country?: string;
    // Add device info fields
    userAgent?: string;
    platform?: string;
    language?: string;
    timezone?: string;
    screenResolution?: string;
    viewport?: string;
    // Add location fields from client-side geolocation
    location?: {
      country?: string;
      city?: string;
      region?: string;
      timezone?: string;
    };
    sandboxMode?: boolean;
  };
}

interface SessionCompletionResponse {
  success: boolean;
  message: string;
  sessionId: string;
  status: 'verified' | 'failed' | 'cancelled';
  verified: boolean;
  accessGranted?: boolean;
  completedAt: string;
  externalUserId?: string | null;
}

/**
 * Outcome of reporting a session result. Only `confirmed` means Portal
 * durably recorded the requested status; every other outcome must not be
 * presented as that result.
 *
 * - `confirmed`: `/complete` acknowledged the status, or the authenticated
 *   lifecycle read shows the session already VERIFIED (a committed
 *   completion whose response was lost).
 * - `expired`: Portal rejected completion because the session expired.
 * - `reconciled`: Portal settled differently; render `lifecycle`.
 * - `rejected`: a definitive rejection Portal state could not explain.
 * - `unconfirmed`: the outcome is unknown after the bounded retry budget.
 */
export type SessionCompletionOutcome =
  | {
      kind: 'confirmed';
      status: SessionResult['status'];
      response: SessionCompletionResponse | null;
    }
  | { kind: 'expired' }
  | { kind: 'reconciled'; lifecycle: LifecycleProjection }
  | { kind: 'rejected'; status: number }
  | { kind: 'unconfirmed' };

/**
 * Completion retry policy. `/complete` is idempotent, so an ambiguous
 * attempt (5xx, timeout, network loss) replays the identical request.
 * Exponential backoff with jitter, a server `Retry-After` hint capped per
 * wait, and a total budget that never outlives the session's known expiry.
 */
export const COMPLETION_RETRY_POLICY = {
  initialDelayMs: 1_000,
  maxDelayMs: 8_000,
  maxServerDelayMs: 10_000,
  totalBudgetMs: 45_000,
  requestTimeoutMs: 10_000,
  lifecycleReadTimeoutMs: 5_000,
  expiryMarginMs: 1_000,
} as const;

type CompletionAttemptResult =
  | { kind: 'response'; ok: boolean; status: number; data: unknown; retryAfterMs: number | null }
  | { kind: 'transport' };

const parseRetryAfterMs = (
  headerValue: string | null | undefined,
  bodySeconds: unknown,
): number | null => {
  if (headerValue) {
    const seconds = Number(headerValue);
    if (Number.isFinite(seconds) && seconds >= 0) return seconds * 1000;
    const date = Date.parse(headerValue);
    if (Number.isFinite(date)) return Math.max(0, date - Date.now());
  }
  if (typeof bodySeconds === 'number' && Number.isFinite(bodySeconds) && bodySeconds >= 0) {
    return bodySeconds * 1000;
  }
  return null;
};

const responseMessage = (data: unknown): string => {
  if (!data || typeof data !== 'object') return '';
  const message = (data as { message?: unknown }).message;
  if (typeof message === 'string') return message;
  if (Array.isArray(message)) return message.filter((m) => typeof m === 'string').join(' ');
  return '';
};

const isTransientCompletionStatus = (status: number): boolean =>
  status === 408 || status === 429 || status >= 500;

type LifecycleRead =
  | { settled: SessionCompletionOutcome }
  | { pending: LifecycleProjection };

/**
 * Whether Portal still reports the exact lifecycle/attempt/operation fence the
 * completion body was captured with.
 */
const fenceStillCurrent = (body: string, lifecycle: LifecycleProjection): boolean => {
  let fence: Record<string, unknown>;
  try {
    fence = JSON.parse(body) as Record<string, unknown>;
  } catch {
    return false;
  }
  return (
    typeof fence.lifecycleVersion === 'number' &&
    fence.lifecycleVersion === lifecycle.lifecycleVersion &&
    fence.workflowAttemptId === lifecycle.attempt?.id &&
    fence.workflowAttemptGeneration === lifecycle.attempt?.generation &&
    fence.operationId === lifecycle.attempt?.operation?.id &&
    fence.operationGeneration === lifecycle.attempt?.operation?.generation
  );
};

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export interface SessionStatusDetails {
  status?: string;
  progress?: number;
  updatedAt?: string;
  terminal?: boolean;
  success?: boolean | null;
  verificationMethod?: string;
  verified: boolean;
  error?: string;
}

/**
 * Session Reporting Service Class
 *
 * Manages the reporting of verification session results to the portal-api.
 * Includes device information collection, duplicate prevention, and graceful
 * error handling to ensure reporting doesn't disrupt the user experience.
 */
export class SessionReportingService {
  private reportedSessions: Set<string> = new Set();
  /** Single owner per session/status; settled definitive outcomes are kept. */
  private completionOwners = new Map<string, Promise<SessionCompletionOutcome>>();
  /** Request body captured at first send and replayed unchanged on retry. */
  private completionBodies = new Map<string, string>();
  private apiUrl: string | null = null;
  private apiKey: string | null = null;
  private sessionToken: string | null = null;
  private sandboxMode: boolean | null = null;

  constructor() {
    // Configuration will be loaded on first use
  }

  async markVerificationStarted(sessionId?: string): Promise<boolean> {
    void sessionId;
    try {
      await verificationLifecycleService.startIfNeeded();
      return true;
    } catch (error) {
      reportingLogger.warn(
        'Unable to confirm verification start',
        error instanceof Error ? error : new Error(String(error)),
      );
      return false;
    }
  }

  /**
   * Ensure configuration is loaded from runtime config
   *
   * Loads API URL and authentication details from runtime configuration
   * with fallback to localhost for development environments.
   *
   * @private
   */
  private async ensureConfigLoaded() {
    try {
      const config = await runtimeConfig.loadConfig();
      this.apiUrl = config.apiUrl;
      this.apiKey = config.apiKey || null;
      this.sessionToken = config.sessionToken || null;
      this.sandboxMode = config.features?.sandboxMode ?? null;
    } catch (error) {
      reportingLogger.error('Failed to load runtime configuration:', error instanceof Error ? error : new Error(`${error}`));
      // Never keep stale session-bound auth after a failed reload.
      this.apiUrl = 'http://localhost:3001';
      this.apiKey = null;
      this.sessionToken = null;
      this.sandboxMode = null;
    }
  }

  /**
   * Get client location from IP geolocation service
   *
   * Fetches the client's geographic location using ipapi.co service.
   * Includes a 2-second timeout to prevent blocking the verification flow.
   *
   * @returns {Promise<Object>} Location information object
   * @returns {string} returns.country - Country code (e.g., "US")
   * @returns {string} returns.city - City name
   * @returns {string} returns.region - State/Province name
   * @returns {string} returns.timezone - Timezone name
   * @private
   */
  private async getClientLocation() {
    try {
      // Set a 2-second timeout for geolocation
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 2000);
      
      reportingLogger.info('Fetching client location from ipapi.co');
      const response = await fetch('https://ipapi.co/json/', {
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`Geolocation failed: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Check for API errors
      if (data.error) {
        reportingLogger.warn('ipapi.co returned error:', { labels: { reason: data.reason }});
        throw new Error(data.reason);
      }

      const location = {
        country: data.country_code || 'Unknown',
        city: data.city || 'Unknown',
        region: data.region || 'Unknown',
        timezone:
          data.timezone ||
          Intl.DateTimeFormat().resolvedOptions().timeZone ||
          'Unknown',
      };

      reportingLogger.info('Client location retrieved:', { labels: { location: JSON.stringify(location) }});
      return location;
    } catch (error) {
      // Don't let geolocation failures block the flow
      if (error instanceof Error && error.name === 'AbortError') {
        reportingLogger.warn('Geolocation timeout after 2 seconds');
      } else {
        reportingLogger.warn('Geolocation failed:', error instanceof Error ? error : new Error(`${error}`));
      }
      
      // Return unknown location on failure
      return {
        country: 'Unknown',
        city: 'Unknown',
        region: 'Unknown',
        timezone:
          Intl.DateTimeFormat().resolvedOptions().timeZone ||
          'Unknown',
      };
    }
  }

  /**
   * Get device information from browser
   *
   * Collects comprehensive device and browser information for analytics
   * and fraud detection purposes.
   *
   * @returns {Object} Device information object
   * @returns {string} returns.userAgent - Browser user agent string
   * @returns {string} returns.platform - Operating system platform
   * @returns {string} returns.language - Browser language setting
   * @returns {string} returns.timezone - User's timezone
   * @returns {string} returns.screenResolution - Screen resolution
   * @returns {string} returns.viewport - Browser viewport dimensions
   * @private
   */
  private getDeviceInfo() {
    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
    };
  }

  /**
   * Check session status from portal-api
   *
   * Queries the current status of a verification session to determine if
   * it has already been verified (e.g., through Emblem authentication).
   *
   * @param {string} sessionId - Session ID to check
   * @returns {Promise<{verified: boolean, verificationMethod?: string}>} Session status
   * @public
   */
  async getSessionStatus(sessionId: string): Promise<SessionStatusDetails> {
    // Ensure configuration is loaded
    await this.ensureConfigLoaded();

    if (!this.apiKey) {
      reportingLogger.warn('API key not available, cannot check session status');
      return { verified: false, error: 'No API key available' };
    }

    try {
      reportingLogger.info('Checking session status:', { labels: { sessionId }});

      const headers: Record<string, string> = {
        'X-API-Key': this.apiKey,
      };
      const devBrand = getDevBrandSlug();
      if (devBrand) {
        headers['X-Brand-Override'] = devBrand;
      }

      const response = await fetch(
        `${this.apiUrl}/api/v1/sessions/${sessionId}/status`,
        {
          method: 'GET',
          headers,
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        reportingLogger.error('Failed to check session status:', {
          labels: {
            status: `${response.status}`,
            error: errorText,
          }
        });
        return { verified: false, error: `HTTP ${response.status}` };
      }

      const data = await response.json();
      reportingLogger.info('Session status retrieved:', { labels: { data: JSON.stringify(data) }});

      const status = typeof data.status === 'string' ? data.status : undefined;
      const verified = status === 'VERIFIED' || data.verified === true || data.success === true;

      return {
        status,
        progress: typeof data.progress === 'number' ? data.progress : undefined,
        updatedAt:
          typeof data.updatedAt === 'string'
            ? data.updatedAt
            : data.updatedAt instanceof Date
              ? data.updatedAt.toISOString()
              : undefined,
        terminal: typeof data.terminal === 'boolean' ? data.terminal : undefined,
        success:
          typeof data.success === 'boolean' || data.success === null
            ? data.success
            : undefined,
        verificationMethod: data.verificationMethod,
        verified,
      };
    } catch (error) {
      reportingLogger.error('Error checking session status:', error instanceof Error ? error : new Error(`${error}`));
      return { verified: false, error: 'Network error' };
    }
  }

  async checkSessionStatus(sessionId: string): Promise<{
    verified: boolean;
    verificationMethod?: string;
    error?: string;
  }> {
    const status = await this.getSessionStatus(sessionId);

    return {
      verified: status.verified,
      verificationMethod: status.verificationMethod,
      error: status.error,
    };
  }

  /**
   * Report session result to portal-api
   *
   * Compatibility wrapper around {@link reportSessionCompletion}: resolves to
   * the server-confirmed completion response, or null when Portal has not
   * confirmed the requested status.
   *
   * @param {SessionResult} result - Session result data to report
   * @returns {Promise<SessionCompletionResponse | null>} Server-confirmed completion response
   * @public
   */
  async reportSessionResult(result: SessionResult): Promise<SessionCompletionResponse | null> {
    const outcome = await this.reportSessionCompletion(result);
    if (outcome.kind !== 'confirmed') return null;
    return outcome.response ?? {
      success: true,
      message: 'Session status confirmed by lifecycle',
      sessionId: result.sessionId,
      status: outcome.status,
      verified: outcome.status === 'verified',
      completedAt: '',
    };
  }

  /**
   * Report a session result and establish Portal's durable outcome.
   *
   * One in-flight owner exists per session and status: concurrent callers
   * (StrictMode effects, consecutive screens) join it, and a settled
   * definitive outcome is returned to later duplicates without another
   * request. An `unconfirmed` outcome is not retained, so an explicit retry
   * runs again with the identical request body captured at first send.
   */
  reportSessionCompletion(result: SessionResult): Promise<SessionCompletionOutcome> {
    const reportKey = `${result.sessionId}-${result.status}`;
    const existing = this.completionOwners.get(reportKey);
    if (existing) {
      reportingLogger.info('Joining existing session completion report', {
        labels: { sessionId: result.sessionId, status: result.status },
      });
      return existing;
    }

    const owner = this.runCompletion(reportKey, result).catch((error: unknown) => {
      reportingLogger.error(
        'Session completion reporting failed unexpectedly:',
        error instanceof Error ? error : new Error(`${error}`),
      );
      return { kind: 'unconfirmed' } as SessionCompletionOutcome;
    }).then((outcome) => {
      const ownsKey = this.completionOwners.get(reportKey) === owner;
      if (outcome.kind === 'unconfirmed') {
        // Same report, same fence: an explicit retry replays the body.
        if (ownsKey) this.completionOwners.delete(reportKey);
      } else if (
        outcome.kind === 'rejected' ||
        (outcome.kind === 'reconciled' && outcome.lifecycle.sessionStatus === 'PENDING')
      ) {
        // The session is still active on a different fence: a later report
        // (after the user completes the step Portal selected) must be sent
        // fresh with the then-current fence, not answered from this cache.
        if (ownsKey) this.completionOwners.delete(reportKey);
        this.completionBodies.delete(reportKey);
      } else {
        // confirmed, expired or a terminal reconciliation is final.
        this.completionBodies.delete(reportKey);
      }
      return outcome;
    });
    this.completionOwners.set(reportKey, owner);
    return owner;
  }

  private async runCompletion(
    reportKey: string,
    result: SessionResult,
  ): Promise<SessionCompletionOutcome> {
    // Ensure configuration is loaded
    await this.ensureConfigLoaded();

    const startedAt = Date.now();
    const deadline = this.completionDeadline(result.sessionId, startedAt);
    if (Date.now() >= deadline) {
      const terminal = await recoverTerminalReturn(result.sessionId, true);
      if (terminal?.status === 'verified') return { kind: 'confirmed', status: 'verified', response: null };
      return { kind: 'unconfirmed' };
    }


    if (!this.apiKey) {
      reportingLogger.warn('API key not available, confirming session status through lifecycle only');
      const read = await this.readLifecycle(result);
      return read && 'settled' in read ? read.settled : { kind: 'unconfirmed' };
    }

    const url = `${this.apiUrl}/api/v1/sessions/${result.sessionId}/complete`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-API-Key': this.apiKey,
    };
    if (this.sessionToken) {
      headers.Authorization = `Bearer ${this.sessionToken}`;
    }
    const devBrand = getDevBrandSlug();
    if (devBrand) {
      headers['X-Brand-Override'] = devBrand;
    }

    // The lifecycle/attempt/operation fence is captured once. Retries replay
    // this exact body; they never re-read the current lifecycle for it.
    let body = this.completionBodies.get(reportKey);
    if (!body) {
      body = await this.buildCompletionBody(result);
      this.completionBodies.set(reportKey, body);
    }

    let attempt = 0;
    for (;;) {
      attempt += 1;
      const response = await this.sendCompletion(url, headers, body);
      const status = response.kind === 'response' ? response.status : 0;

      if (response.kind === 'response' && response.ok) {
        const data = response.data as SessionCompletionResponse | null;
        reportingLogger.info('Session result reported successfully:', { labels: { data: JSON.stringify(data) }});
        const confirmed = !!data && data.status === result.status &&
          (result.status !== 'verified' || data.verified === true);
        if (confirmed) {
          return { kind: 'confirmed', status: result.status, response: data };
        }
        // Portal holds a different durable outcome; render what it says.
        const read = await this.readLifecycle(result);
        if (read && 'settled' in read) return read.settled;
        return read ? { kind: 'reconciled', lifecycle: read.pending } : { kind: 'rejected', status };
      }

      if (response.kind === 'response' && status === 400 &&
          /session has expired/i.test(responseMessage(response.data))) {
        reportingLogger.warn('Session completion rejected: session has expired', {
          labels: { sessionId: result.sessionId },
        });
        const terminal = await recoverTerminalReturn(result.sessionId, true);
        if (terminal?.status === 'verified') return { kind: 'confirmed', status: 'verified', response: null };
        return { kind: 'expired' };
      }

      if (response.kind === 'response' && status === 409) {
        // Never replay blindly: Portal's lifecycle decides. Only a conflict on
        // a fence Portal still reports as current (a concurrent transaction,
        // not stale state) replays the identical request.
        reportingLogger.warn('Session completion conflicted; reconciling with lifecycle', {
          labels: { sessionId: result.sessionId, attempt: `${attempt}` },
        });
        const read = await this.readLifecycleUntilAvailable(result, deadline);
        if (!read) return { kind: 'unconfirmed' };
        if ('settled' in read) return read.settled;
        if (!fenceStillCurrent(body, read.pending)) {
          return { kind: 'reconciled', lifecycle: read.pending };
        }
      } else if (response.kind === 'response' && !isTransientCompletionStatus(status)) {
        // Definitive rejection (authorization, not found, invalid request). Do
        // not replay; Portal's lifecycle decides what the user sees.
        reportingLogger.warn('Session completion rejected; reconciling with lifecycle', {
          labels: { sessionId: result.sessionId, status: `${status}` },
        });
        const read = await this.readLifecycle(result);
        return read && 'settled' in read ? read.settled : { kind: 'rejected', status };
      } else {
        // Outcome unknown: the write may or may not have committed. A
        // lifecycle read showing VERIFIED confirms a lost acknowledgement.
        reportingLogger.warn('Session completion outcome unknown; will reconcile and retry', {
          labels: {
            sessionId: result.sessionId,
            attempt: `${attempt}`,
            status: response.kind === 'response' ? `${status}` : 'transport_error',
          },
        });
        const read = await this.readLifecycle(result);
        if (read && 'settled' in read) return read.settled;
      }

      const delay = this.retryDelay(
        attempt,
        response.kind === 'response' ? response.retryAfterMs : null,
        status === 429,
      );
      if (Date.now() + delay >= deadline) {
        reportingLogger.warn('Session completion retry budget exhausted without a confirmed outcome', {
          labels: { sessionId: result.sessionId, attempts: `${attempt}` },
        });
        return { kind: 'unconfirmed' };
      }
      await sleep(delay);
    }
  }

  private async buildCompletionBody(result: SessionResult): Promise<string> {
    // Get location and device info in parallel
    const [location, deviceInfo] = await Promise.all([
      this.getClientLocation(),
      Promise.resolve(this.getDeviceInfo())
    ]);

    // Add location and device info to verification data
    const enrichedVerificationData = sanitizeBrowserMetadata({
      ...result.verificationData,
      ...deviceInfo,
      location, // Add location data
      sandboxMode:
        result.verificationData?.sandboxMode ?? this.sandboxMode ?? undefined,
    });

    reportingLogger.info('Reporting session result:', {
      labels: {
        sessionId: result.sessionId,
        status: result.status,
        hasDeviceInfo: `${true}`,
        hasLocation: `${true}`,
        location: JSON.stringify(location),
        url: `${this.apiUrl}/api/v1/sessions/${result.sessionId}/complete`,
      }
    });

    const lifecycle = verificationLifecycleService.getCurrent();
    return JSON.stringify({
      status: result.status,
      metadata: enrichedVerificationData || {},
      ...(lifecycle ? {
        lifecycleVersion: lifecycle.lifecycleVersion,
        workflowAttemptId: lifecycle.attempt?.id,
        workflowAttemptGeneration: lifecycle.attempt?.generation,
        operationId: lifecycle.attempt?.operation?.id,
        operationGeneration: lifecycle.attempt?.operation?.generation,
      } : {}),
    });
  }

  private async sendCompletion(
    url: string,
    headers: Record<string, string>,
    body: string,
  ): Promise<CompletionAttemptResult> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), COMPLETION_RETRY_POLICY.requestTimeoutMs);
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body,
        signal: controller.signal,
      });
      const data: unknown = await response.json().catch(() => null);
      const responseHeaders = response.headers as Headers | undefined;
      return {
        kind: 'response',
        ok: response.ok,
        status: response.status,
        data,
        retryAfterMs: parseRetryAfterMs(
          responseHeaders?.get?.('Retry-After'),
          data && typeof data === 'object'
            ? (data as { retryAfterSeconds?: unknown }).retryAfterSeconds
            : undefined,
        ),
      };
    } catch (error) {
      reportingLogger.warn('Session completion request did not complete:', {
        labels: { error: error instanceof Error ? error.name : 'unknown' },
      });
      return { kind: 'transport' };
    } finally {
      clearTimeout(timer);
    }
  }

  /**
   * Read the authenticated lifecycle once. VERIFIED confirms a committed
   * completion even when the `/complete` response was lost; any other
   * non-pending status is Portal's settled answer, as is a definitive
   * rejection of the read. Null means the read itself did not succeed.
   */
  private async readLifecycle(result: SessionResult): Promise<LifecycleRead | null> {
    let lifecycle: LifecycleProjection;
    try {
      lifecycle = await verificationLifecycleService.reconcile(
        COMPLETION_RETRY_POLICY.lifecycleReadTimeoutMs,
      );
    } catch (error) {
      if (isExpiredLifecycleError(error)) {
        const terminal = await recoverTerminalReturn(result.sessionId, true);
        if (terminal?.status === 'verified') return { settled: { kind: 'confirmed', status: 'verified', response: null } };
        return { settled: { kind: 'expired' } };
      }
      if (isDefinitiveLifecycleRejection(error)) {
        return {
          settled: {
            kind: 'rejected',
            status: error instanceof LifecycleRequestError ? error.status : 409,
          },
        };
      }
      return null;
    }
    if (lifecycle.sessionId !== result.sessionId) return null;
    if (lifecycle.sessionStatus === 'VERIFIED' && result.status === 'verified') {
      reportingLogger.info('Lifecycle confirms the session is verified', {
        labels: { sessionId: result.sessionId },
      });
      return { settled: { kind: 'confirmed', status: 'verified', response: null } };
    }
    if (lifecycle.sessionStatus !== 'PENDING') {
      return { settled: { kind: 'reconciled', lifecycle } };
    }
    return { pending: lifecycle };
  }

  private async readLifecycleUntilAvailable(
    result: SessionResult,
    deadline: number,
  ): Promise<LifecycleRead | null> {
    for (let attempt = 1; ; attempt += 1) {
      const read = await this.readLifecycle(result);
      if (read) return read;
      const delay = this.retryDelay(attempt, null, false);
      if (Date.now() + delay >= deadline) return null;
      await sleep(delay);
    }
  }

  private retryDelay(attempt: number, retryAfterMs: number | null, rateLimited: boolean): number {
    const policy = COMPLETION_RETRY_POLICY;
    const exponential = Math.min(policy.initialDelayMs * 2 ** (attempt - 1), policy.maxDelayMs);
    // Jitter within 75-100% of the step spreads retries across clients while
    // keeping a sustained outage to roughly ten attempts per budget.
    const backoff = exponential * (0.75 + Math.random() * 0.25);
    if (retryAfterMs === null) return backoff;
    // A rate limit is honored in full (no hammering); an availability hint is
    // capped so one response cannot stall the bounded budget.
    const hint = rateLimited ? retryAfterMs : Math.min(retryAfterMs, policy.maxServerDelayMs);
    return Math.max(backoff, hint);
  }

  private completionDeadline(sessionId: string, startedAt: number): number {
    const budgetDeadline = startedAt + COMPLETION_RETRY_POLICY.totalBudgetMs;
    const lifecycle = verificationLifecycleService.getCurrent();
    const expiresAt = lifecycle?.sessionId === sessionId ? Date.parse(lifecycle.expiresAt) : NaN;
    return Number.isFinite(expiresAt)
      ? Math.min(budgetDeadline, expiresAt - COMPLETION_RETRY_POLICY.expiryMarginMs)
      : budgetDeadline;
  }

  /**
   * Report a cancellation result to portal-api
   *
   * Uses a minimal payload and keepalive to increase the chance that
   * cancellation is recorded when the verification window is closed.
   */
  async reportSessionCancellation(
    sessionId: string,
    reason: string = 'cancelled'
  ): Promise<SessionCompletionResponse | null> {
    await this.ensureConfigLoaded();

    if (!this.apiKey || !this.apiUrl) {
      reportingLogger.warn('API key or API URL not available, skipping cancellation reporting');
      return null;
    }

    const reportKey = `${sessionId}-cancelled`;
    if (this.reportedSessions.has(reportKey)) {
      reportingLogger.warn('Cancellation already reported, skipping duplicate', {
        labels: { sessionId },
      });
      return null;
    }

    this.reportedSessions.add(reportKey);

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-API-Key': this.apiKey,
      };
      if (this.sessionToken) {
        headers.Authorization = `Bearer ${this.sessionToken}`;
      }
      const devBrand = getDevBrandSlug();
      if (devBrand) {
        headers['X-Brand-Override'] = devBrand;
      }

      const response = await fetch(
        `${this.apiUrl}/api/v1/sessions/${sessionId}/complete`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            status: 'cancelled',
            metadata: {
              reason,
              ...this.getDeviceInfo(),
            },
          }),
          keepalive: true,
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `Failed to report cancellation: ${response.status} ${errorText}`
        );
      }

      const data = await response.json();
      reportingLogger.info('Session cancellation reported successfully', {
        labels: { sessionId },
      });
      return data as SessionCompletionResponse;
    } catch (error) {
      reportingLogger.error(
        'Failed to report cancellation:',
        error instanceof Error ? error : new Error(`${error}`)
      );
      this.reportedSessions.delete(reportKey);
      return null;
    }
  }
}

// Export singleton instance
export const sessionReportingService = new SessionReportingService();
